/**
 * Big Picture: the console mode (`03-client-app.md` section 4.6).
 *
 * The app is already a console on a desk — a rail, a clock, a focus ring, a hints bar. Big
 * Picture is the same console across a room: it takes the whole screen, scales the interface for
 * the distance it is being read from, keeps the controller hints up whatever was last touched,
 * and takes the mouse pointer away once a stick is doing the driving.
 *
 * It is one attribute on the document and one call to the shell. Everything that makes a screen
 * look right at three metres is CSS reading that attribute (`styles/big-picture.css`), which is
 * what keeps this from becoming a second copy of every screen in the app.
 */

import * as React from 'react';
import { capabilities, isDesktop, isTauri, platform } from '../platform/index.js';
import { native } from '../platform/tauri.js';
import { lastInputSource, onInputSourceChange } from '../gamepad/input.js';
import { getSettings, onSettingsChange } from './settings.js';

/**
 * What launching the app should do.
 *
 * `auto` is the reason this is three states rather than a switch: the right default is not the
 * same on every desktop, and a switch would make somebody on a Steam Deck turn it on by hand on
 * a machine that has no keyboard to turn it on with.
 */
export type BigPictureLaunch = 'auto' | 'on' | 'off';

/**
 * What `auto` means here.
 *
 * Linux is the handheld platform: the Steam Deck runs this app from Gaming Mode, where there is
 * no desktop behind the window to share the screen with and a controller is the only input
 * there is. Windows is a desk until somebody says otherwise.
 *
 * Deliberately every Linux desktop rather than only a detected Deck. The detection needs the
 * shell to answer, which happens after the first paint, so keying the launch mode on it would
 * mean opening in one mode and changing to the other in front of the user; and a Linux desktop
 * that is not a Deck is still more likely to be a machine plugged into a television than a
 * Windows one. Anybody it is wrong for turns it off once, in `/settings`.
 */
export function launchDefault(): boolean {
  if (!capabilities.bigPicture) return false;
  const setting = getSettings().bigPictureOnLaunch;
  if (setting === 'on') return true;
  if (setting === 'off') return false;
  return platform === 'linux';
}

let active = false;
type Listener = (active: boolean) => void;
const listeners = new Set<Listener>();

/**
 * Mirrors the mode onto the document, which is where the stylesheet reads it.
 *
 * The input source goes up with it, because the pointer is only in the way once a controller is
 * driving: a mouse moved in Big Picture has to bring its cursor back, or the mode is a trap for
 * anybody who reached for the mouse.
 */
function applyToDocument(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.bigPicture = String(active);
  document.documentElement.dataset.inputSource = lastInputSource();
}

/**
 * Takes the screen, or gives it back.
 *
 * Three shells, one call. The apps ask the Tauri window, because a webview cannot take a screen
 * it was not given permission to and should not be given it. The web asks the browser, which
 * only answers inside a user gesture — that is why this is never called from a launch effect on
 * the web, and why a refusal is swallowed rather than reported: the mode still works, it is
 * simply in a window.
 */
async function takeScreen(wanted: boolean): Promise<void> {
  if (isDesktop && isTauri()) {
    await native.windowSetFullscreen(wanted);
    return;
  }
  if (typeof document === 'undefined') return;
  if (wanted) {
    if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
  } else if (document.fullscreenElement) {
    await document.exitFullscreen();
  }
}

/**
 * Enters or leaves Big Picture.
 *
 * The attribute is set before the screen is asked for, so the interface has already scaled by
 * the time the window changes size rather than reflowing afterwards in front of the user.
 */
export function setBigPicture(next: boolean): void {
  if (active === next) return;
  active = next;
  applyToDocument();
  for (const listener of listeners) listener(active);
  void takeScreen(next).catch(() => {
    // A shell older than the command, or a browser that refused outside a gesture. The mode is
    // what the attribute says; fullscreen is what it asked for. Losing the second does not make
    // the first wrong, and there is nothing here worth interrupting anybody over.
  });
}

export function toggleBigPicture(): void {
  setBigPicture(!active);
}

/**
 * Starts the mode and keeps it honest for the life of the app.
 *
 * Three things have to stay in step, and all three of them are here so that no screen has to
 * know about any of them:
 *
 * - the launch default, applied once;
 * - the input source on the document, so the pointer can come back when a mouse moves;
 * - the browser's own fullscreen, which Escape can leave without asking us first.
 *
 * Called once, by the app shell. Returns the teardown.
 */
export function startBigPicture(): () => void {
  applyToDocument();
  setBigPicture(launchDefault());

  const offInput = onInputSourceChange(() => applyToDocument());

  // Escape leaves the browser's fullscreen without going through us, and on a desktop the window
  // manager's own fullscreen key does the same. A mode that says it is on while the window is
  // back in a frame is worse than one that follows: the exit is right there and it did nothing.
  const onFullscreenChange = () => {
    if (active && !document.fullscreenElement) setBigPicture(false);
  };
  const watchesBrowserFullscreen = !isTauri();
  if (watchesBrowserFullscreen) {
    document.addEventListener('fullscreenchange', onFullscreenChange);
  }

  // Changing the launch preference to `on` or `off` in `/settings` is somebody saying what they
  // want now, not only next time. `auto` is left alone: it describes a launch, and re-resolving
  // it would drag a Linux desktop back into the mode it was just taken out of.
  //
  // Only a change to this one preference counts. The listener hears every settings write, so
  // without the comparison, leaving the mode by hand on a machine set to `on` and then touching
  // any unrelated switch would put it straight back — a screen where turning the wallpaper off
  // takes the whole window with it.
  let lastPreference = getSettings().bigPictureOnLaunch;
  const offSettings = onSettingsChange((settings) => {
    const preference = settings.bigPictureOnLaunch;
    if (preference === lastPreference) return;
    lastPreference = preference;
    if (preference === 'on') setBigPicture(true);
    if (preference === 'off') setBigPicture(false);
  });

  return () => {
    offInput();
    offSettings();
    if (watchesBrowserFullscreen) {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    }
  };
}

/** Whether Big Picture is on, for the components that draw differently in it. */
export function useBigPicture(): boolean {
  const [state, setState] = React.useState(active);
  React.useEffect(() => {
    listeners.add(setState);
    // The launch default may have been applied before this component existed.
    setState(active);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return state;
}
